<template>
  <div class="pa-4">
    <div class="d-flex flex-column ga-1">
      <span><b>Status:</b> {{ store.plan.status }}</span>
      <span><b>Created On:</b> {{ store.plan.created.toLocaleString() }}</span>
      <span v-if="publishedName">
        <b>Published By:</b> {{ publishedName }}
      </span>
      <span v-if="store.plan.published_timestamp">
        <b>Published On:</b>
        {{ store.plan.published_timestamp.toLocaleString() }}
      </span>
    </div>
    <div class="mt-4 d-flex align-center">
      <v-text-field
        class="revision-input"
        v-model="store.plan.revision"
        density="compact"
        variant="outlined"
        label="Revision"
        :readonly="readonly"
        hide-details
      />
      <slot name="revision-actions" />
    </div>

    <v-table
      v-if="store.plan.asset_management_plan_master.approval_stages.length > 0"
      class="mt-4"
    >
      <thead>
        <tr>
          <th>Order</th>
          <th>Approvers</th>
          <th>Status</th>
          <th v-if="showApproval">Approval</th>
          <th>Comment</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="stage in store.plan.asset_management_plan_master
            .approval_stages"
          :key="stage.id"
        >
          <td>{{ stage.order + 1 }}</td>
          <td>{{ stage.formatApprovers(users) }}</td>
          <td>
            <v-tooltip location="bottom" :text="stage.status_tooltip">
              <template #activator="{ props }">
                <v-chip
                  v-bind="stage.status_tooltip ? props : {}"
                  :color="
                    stage.status === 'Approved'
                      ? 'green'
                      : stage.status === 'Rejected'
                      ? 'red'
                      : ''
                  "
                  label
                >
                  {{ stage.status }}
                </v-chip>
              </template>
            </v-tooltip>
          </td>
          <td v-if="showApproval">
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="green"
                  icon="mdi-check"
                  variant="text"
                  density="compact"
                  :disabled="!approvable(stage)"
                  @click="openApprovalDialog(true)"
                />
              </template>
              <span>Approve</span>
            </v-tooltip>
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="ml-1"
                  color="red"
                  icon="mdi-close"
                  variant="text"
                  density="compact"
                  :disabled="!approvable(stage)"
                  @click="openApprovalDialog(false)"
                />
              </template>
              <span>Reject</span>
            </v-tooltip>
          </td>
          <td>{{ stage.comment }}</td>
        </tr>
      </tbody>
    </v-table>

    <BaseDialog
      v-model="approvalDialog"
      max-width="400"
      :title="`${approvingMode ? 'Approve' : 'Reject'} Revision`"
    >
      <v-text-field
        v-model="approvalName"
        label="Your Name"
        density="compact"
        variant="outlined"
        hide-details
      />
      <v-text-field
        class="mt-2"
        v-model="approvalComment"
        label="Comment"
        density="compact"
        variant="outlined"
        hide-details
      />
      <small class="d-block mt-2">
        I confirm that I am {{ approvingMode ? "approving" : "rejecting" }} this
        revision.
      </small>
      <template #actions>
        <v-btn
          :disabled="!approvalName"
          color="primary"
          variant="text"
          :loading="confirmingApproval"
          @click="confirmApproval"
        >
          Confirm
        </v-btn>
      </template>
    </BaseDialog>
  </div>
</template>

<script setup lang="ts">
import { AssetManagementPlanApproval, User } from "@/classes";
import type { AssetManagementPlanMasterApprovalStage } from "@/classes/asset-management-plan";
import { useAMPStore } from "@/store/asset-management-plan";
import { useAuthStore } from "@/store/auth";
import { useListStore } from "@/store/lists";
import { computed, ref } from "vue";
import BaseDialog from "../base/dialogs/BaseDialog.vue";

const store = useAMPStore();
const lists = useListStore();
const users = lists.useList<User>("users");

const props = defineProps<{
  readonly: boolean;
}>();

const emit = defineEmits<{
  refresh: [];
}>();

const $auth = useAuthStore();

const showApproval = computed(() => {
  if (store.plan && store.plan.status === "In Review") return true;
  return false;
});

const approvable = (stage: AssetManagementPlanMasterApprovalStage) => {
  if (showApproval.value && store.plan.active_approval_stage?.id === stage.id) {
    return store.plan.active_approval_stage.approvers.some(
      (a) => a.user_id === $auth.state.user?.id,
    );
  }
  return false;
};

const approvalDialog = ref(false);
const approvingMode = ref(false);
const approvalName = ref("");
const approvalComment = ref("");

const openApprovalDialog = (approving: boolean) => {
  approvalDialog.value = true;
  approvingMode.value = approving;
  approvalName.value = `${$auth.state.user.first_name || ""} ${
    $auth.state.user.last_name || ""
  }`.trim();
  approvalComment.value = "";
};

const confirmingApproval = ref(false);
const confirmApproval = async () => {
  if (store.plan && store.plan.active_approval_stage) {
    confirmingApproval.value = true;
    const approval =
      store.plan.active_approval_stage.current_approval ||
      new AssetManagementPlanApproval();
    approval.stage_id = store.plan.active_approval_stage.id;
    approval.asset_management_plan_id = store.plan.id;
    approval.name = approvalName.value;
    approval.comment = approvalComment.value;
    approval.timestamp = new Date();
    approval.rejected = !approvingMode.value;
    approval.done = true;
    await approval.save();
    confirmingApproval.value = false;
    approvalDialog.value = false;
    emit("refresh");
  }
};

const publishedName = computed(() => {
  return (
    users.value.find((user) => user.id === store.plan.publisher_id)?.name || ""
  );
});
</script>

<style lang="scss" scoped>
.revision-input {
  max-width: 200px;
}
</style>
